import React, { useMemo, useState } from 'react'
import * as graph from '@adc/sdk'
import classnames from 'classnames/bind'
import { ComposedChart } from '@adc/chart'
import ms from 'ms'
import '@adc/chart/lib/styles.css'
import { Loader, Text, coreHooks } from '@adc/ui-morpheus'
import _ from 'lodash'
import type { Maybe, NotificationReport } from '@adc/sdk/dist/types'
import { getNotificationsReport } from '../../providers/communications'
import styles from './styles.scss'

const { NotificationStatus } = graph.enums

const cx = classnames.bind(styles)

const STATUSES = Object.values(NotificationStatus)

const categories = STATUSES.map(status => ({
  name: status,
  component: 'bar' as const,
}))
const colorPalette = [
  'blue',
  'emerald',
  'pink',
  'amber',
  'gray',
  'violet',
  'cyan',
  'lime',
  'fuchsia',
] as const
const colors = colorPalette.slice(0, categories.length)

const SUCCESSFUL_CATEGORY = 'Successful'
const ATTEMPTED_CATEGORY = 'Attempted'

// Always include all statuses, even if zero
// For grouped bar: one object with all statuses as keys
// Define which statuses are considered successful/attempted
const SUCCESSFUL_STATUSES = [
  NotificationStatus.Delivered,
  NotificationStatus.Sent,
]
const ATTEMPTED_STATUSES = [
  NotificationStatus.Failed,
  NotificationStatus.Error,
  NotificationStatus.Undelivered,
  NotificationStatus.Deleted,
  NotificationStatus.InProgress,
  NotificationStatus.OptedOut,
  NotificationStatus.Scheduled,
]

const getChartDataFromReport = (report: Maybe<NotificationReport>) => {
  if (!report?.channel_aggregations)
    return { channel: '', totals: [], recent: [] }
  const channelAggs = report.channel_aggregations
  const channel = channelAggs[0]?.channel_type || ''
  const now = new Date()
  const msIn24h = ms('24h')
  const msIn30d = ms('30d')
  // Aggregate by status
  const statusTotals: Record<string, number> = {}
  const status24h: Record<string, number> = {}
  const status30d: Record<string, number> = {}
  STATUSES.forEach(status => {
    statusTotals[status] = 0
    status24h[status] = 0
    status30d[status] = 0
  })
  channelAggs.forEach(agg => {
    const status = agg.status
    const sendDate = agg.send_date ? new Date(agg.send_date) : null
    // Only increment if status is in STATUSES, but always keep all statuses in output
    if (status && STATUSES.includes(status)) {
      statusTotals[status] += agg.total_count
      if (sendDate && now.getTime() - sendDate.getTime() <= msIn24h) {
        status24h[status] += agg.total_count
      }
      if (sendDate && now.getTime() - sendDate.getTime() <= msIn30d) {
        status30d[status] += agg.total_count
      }
    }
  })

  // Show all statuses individually for totals chart
  const totals = [
    {
      name: 'Total',
      ...STATUSES.reduce((acc, status) => {
        acc[status] = statusTotals[status]
        return acc
      }, {} as Record<string, number>),
    },
  ]

  // Group 24h and 30d volumes for each group, using enum values as keys
  const recent = [
    {
      name: 'Sent 24h',
      [SUCCESSFUL_CATEGORY]: STATUSES.filter(s =>
        SUCCESSFUL_STATUSES.includes(s)
      ).reduce((sum, s) => sum + status24h[s], 0),
      [ATTEMPTED_CATEGORY]: STATUSES.filter(s =>
        ATTEMPTED_STATUSES.includes(s)
      ).reduce((sum, s) => sum + status24h[s], 0),
    },
    {
      name: 'Sent 30d',
      [SUCCESSFUL_CATEGORY]: STATUSES.filter(s =>
        SUCCESSFUL_STATUSES.includes(s)
      ).reduce((sum, s) => sum + status30d[s], 0),
      [ATTEMPTED_CATEGORY]: STATUSES.filter(s =>
        ATTEMPTED_STATUSES.includes(s)
      ).reduce((sum, s) => sum + status30d[s], 0),
    },
  ]
  return { channel, totals, recent }
}

export const NotificationReportGraph = ({
  notificationId,
}: {
  notificationId?: string
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [notificationReport, setNotificationReport] =
    useState<NotificationReport | null>(null)

  coreHooks.useMountEffect(() => {
    const fetchNotificationReport = () => {
      if (!notificationId) return
      setIsLoading(true)
      // eslint-disable-next-line no-void
      void getNotificationsReport(notificationId)
        .then(data => setNotificationReport(data))
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line no-void
    void fetchNotificationReport()
  })

  const { totals, recent } = useMemo(
    () => getChartDataFromReport(notificationReport),
    [notificationReport]
  )

  if (isLoading) {
    return (
      <div
        data-elm-id='notification-report-loader'
        className={cx('notification-report-loader')}
      >
        <Loader
          active
          inverted
          inline='centered'
          dimmer={{ style: { position: 'relative' } }}
        >
          <Text color='gray' size='tiny'>
            Loading
          </Text>
        </Loader>
      </div>
    )
  }

  if (!notificationReport || !totals.length) {
    return (
      <Text size='large' color='gray'>
        No data available for this communication.
      </Text>
    )
  }

  return (
    <div
      data-elm-id='notification-report-container'
      className={cx('notification-report-container')}
    >
      <div className={cx('notification-report-graph')}>
        <Text weight='heavy'>Total Messages by Status</Text>
        <ComposedChart
          data={totals}
          index='name'
          categories={STATUSES.map(status => ({
            name: status,
            component: 'bar',
          }))}
          colors={colors}
        />
      </div>
      <div className={cx('notification-report-graph')}>
        <Text weight='heavy'>
          Volume Sent in Past 24h / 30d (Successful vs Attempted)
        </Text>
        <ComposedChart
          data={recent}
          index='name'
          categories={[
            { name: SUCCESSFUL_CATEGORY, component: 'bar' },
            { name: ATTEMPTED_CATEGORY, component: 'bar' },
          ]}
          colors={['emerald', 'amber']}
        />
      </div>
    </div>
  )
}
